// The group info panel — "all of the information about the group".
//
// Agreed with Will 2026-09-10 after reviewing the old SocialTie group page,
// whose right-hand column is what this is. See
// docs/todo-mac-hand-test-2026-09-10.md item 3d for what was kept and what was
// explicitly left out (forums, polls, per-event albums, membership fees).
//
// ⚠ Three of its rows need NO columns and are derived here: the organizer is
// the 999 membership row, the helpers are >= 100, and the count is the array's
// length. Only `about`, `establishedOn`, `memberNoun` and the photo are stored.
//
// Every row omits itself when it has nothing to say. A panel of empty labels is
// worse than a shorter panel, and an unset field is the normal state for a
// group that predates these columns — which, on the day this shipped, was all
// of them.

import { el } from '../dom.js';
import { formatMemberCount, GroupRole, type GroupDetail, type GroupMember } from '@msc/shared';

/**
 * Format a stored 'YYYY-MM-DD' as "December 2015".
 *
 * ⚠ PARSED BY HAND, deliberately. `new Date('2015-12-01')` is read as UTC
 * midnight, so `toLocaleDateString` renders it as NOVEMBER for any viewer west
 * of UTC — the all-day-event bug in CLAUDE.md, in a new place. This is a naive
 * date with no instant behind it, so it must never pass through a Date that
 * carries a zone. Constructing `new Date(y, m - 1, 1)` from the parts is safe:
 * that constructor is LOCAL, so the month cannot drift.
 *
 * Month and year only. Nobody knows the day their poker night began, and the
 * old page showed "December - 2015" for the same reason.
 */
export function formatEstablished(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || month < 1 || month > 12) return ymd;
  const local = new Date(year, month - 1, 1);
  return local.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function row(label: string, value: string | HTMLElement): HTMLElement {
  return el('div', { class: 'ginfo-row' }, [
    el('dt', {}, label),
    el('dd', {}, typeof value === 'string' ? value : [value]),
  ]);
}

/**
 * People are listed by NAME and not linked, deliberately for now: the profile
 * route exists, but the panel's job is "who runs this" at a glance and a row of
 * links reads as navigation. Easy to change if Will wants it.
 */
function nameList(members: GroupMember[]): string {
  return members.map((m) => m.fullName || m.email).join(', ');
}

export function groupInfoPanel(group: GroupDetail, members: GroupMember[]): HTMLElement {
  const owner = members.find((m) => m.role >= GroupRole.OWNER);
  // Admins BELOW owner: the owner is already named on their own row, and
  // listing them twice reads as two different people.
  const helpers = members.filter((m) => m.role >= GroupRole.ADMIN && m.role < GroupRole.OWNER);

  const rows: HTMLElement[] = [];
  if (group.about) rows.push(row("We're about", group.about));
  if (group.establishedOn) rows.push(row('Established', formatEstablished(group.establishedOn)));
  // ⚠ The LABEL stays the static word "Members" while the noun goes in the
  // VALUE. Putting the noun in both read "Card Sharks / 2 Card Sharks" on the
  // first render, which is the sort of thing only a screenshot shows -- the data
  // was perfectly correct. The old SocialTie page put the noun in the label
  // instead ("Number of SocialTie Testers: 11"), which is fine at its column
  // width and wraps to three lines in an 8.5rem label track here.
  rows.push(row('Members', formatMemberCount(group.memberCount, group.memberNoun)));
  if (owner) rows.push(row('Organizer', nameList([owner])));
  if (helpers.length) {
    rows.push(row(helpers.length === 1 ? 'Helper' : 'Helpers', nameList(helpers)));
  }

  const children: HTMLElement[] = [];
  if (group.photoUrl) {
    // ⚠ `alt` names the GROUP rather than describing the picture, because a
    // decorative group photo has no content a screen reader needs — what it
    // needs is to know which group's panel this is.
    children.push(
      el('div', { class: 'ginfo-photo' }, [
        el('img', { src: group.photoUrl, alt: group.name, loading: 'lazy' }),
      ]),
    );
  }
  children.push(el('dl', { class: 'ginfo-rows' }, rows));

  return el('section', { class: 'card ginfo' }, [
    el('h2', { class: 'ginfo-title' }, 'About this group'),
    ...children,
  ]);
}
